"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { Input, Label, Textarea } from "@/components/ui/Input";
import { Card, CardBody } from "@/components/ui/Card";
import { MotionButton } from "@/components/motion";
import { DonateSplitHero } from "@/components/donate/DonateSplitHero";
import { DonateStatsStrip } from "@/components/donate/DonateStatsStrip";
import { DonateStepper } from "@/components/donate/DonateStepper";
import { RecentDonorsTeaser } from "@/components/donate/RecentDonorsTeaser";
import { Reveal } from "@/components/motion/Reveal";
import { useScrollContext } from "@/components/motion/SmoothScrollProvider";
import { useRazorpayCheckout } from "@/hooks/useRazorpayCheckout";
import { api } from "@/lib/api-client";
import type { RecentDonor, Stats } from "@/lib/api-types";
import { site } from "@/content/site";
import {
  formatNameInput,
  isValidPersonName,
} from "@/lib/validators/name";
import {
  formatPanInput,
  isValidPan,
  PAN_THRESHOLD_INR,
} from "@/lib/validators/pan";
import {
  formatPhoneInput,
  isValidIndianMobile,
} from "@/lib/validators/phone";
import { setDonationSuccessCreds } from "@/lib/donation-success-session";
import { cn, formatCurrency } from "@/lib/utils";
import { z } from "zod";

import { DONATION_CATEGORIES } from "@/lib/donation-categories";

const PRESET_AMOUNTS = [500, 1000, 2500, 5000, 10000];

const CATEGORIES = DONATION_CATEGORIES;

type FormErrors = {
  amount?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  pan?: string;
};

const ERROR_FIELD_ORDER: (keyof FormErrors)[] = [
  "amount",
  "firstName",
  "lastName",
  "email",
  "phone",
  "pan",
];

const ERROR_FIELD_IDS: Record<keyof FormErrors, string> = {
  amount: "custom",
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  phone: "phone",
  pan: "pan",
};

function AmountChip({
  amount,
  selected,
  onSelect,
}: {
  amount: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const reduced = useReducedMotion();

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      whileTap={reduced ? undefined : { scale: 0.96 }}
      animate={selected && !reduced ? { scale: [1, 1.03, 1] } : { scale: 1 }}
      transition={{ duration: 0.25 }}
      className={cn(
        "rounded-lg border py-2 text-sm font-medium transition-colors",
        selected
          ? "border-primary bg-primary text-white"
          : "border-border text-foreground hover:border-primary"
      )}
    >
      ₹{amount.toLocaleString("en-IN")}
    </motion.button>
  );
}

function focusFirstError(errs: FormErrors) {
  requestAnimationFrame(() => {
    for (const field of ERROR_FIELD_ORDER) {
      if (errs[field]) {
        document.getElementById(ERROR_FIELD_IDS[field])?.focus();
        break;
      }
    }
  });
}

function parseDonateParams(searchParams: URLSearchParams) {
  const amountParam = searchParams.get("amount");
  let amount = "";
  let customAmount = "";
  if (amountParam) {
    const parsed = parseInt(amountParam, 10);
    if (!Number.isNaN(parsed) && parsed >= 100) {
      if (PRESET_AMOUNTS.includes(parsed)) {
        amount = String(parsed);
      } else {
        customAmount = String(parsed);
      }
    }
  }
  const categoryParam = searchParams.get("category");
  const category =
    categoryParam && CATEGORIES.some((c) => c.value === categoryParam)
      ? categoryParam
      : "general";
  return { amount, customAmount, category };
}

function DonateFormFields() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const allowPaymentTest =
    process.env.NEXT_PUBLIC_USE_MOCK_API === "true" ||
    process.env.NODE_ENV === "development";
  const rawPaymentTest = searchParams.get("paymentTest");
  const paymentTest =
    allowPaymentTest && (rawPaymentTest === "fail" || rawPaymentTest === "cancel")
      ? rawPaymentTest
      : null;
  const parsed = parseDonateParams(searchParams);
  const [amount, setAmount] = useState(parsed.amount);
  const [customAmount, setCustomAmount] = useState(parsed.customAmount);
  const [category, setCategory] = useState(parsed.category);
  const reduced = useReducedMotion();
  const { scrollToElement } = useScrollContext();
  const formCardRef = useRef<HTMLDivElement>(null);
  const { checkout, isProcessing, error, clearError } = useRazorpayCheckout();
  const [donor, setDonor] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    pan: "",
    address: "",
  });
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [recentDonors, setRecentDonors] = useState<RecentDonor[]>([]);
  const [recentDonorsUnavailable, setRecentDonorsUnavailable] = useState(false);
  const [step, setStep] = useState<"amount" | "donor">("amount");
  const [errors, setErrors] = useState<FormErrors>({});

  // Email OTP states
  const [emailOtpDigits, setEmailOtpDigits] = useState<string[]>(Array(6).fill(""));
  const emailOtpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState<string | null>(null);
  const [emailOtpTimer, setEmailOtpTimer] = useState(0);
  const [isSendingEmailOtp, setIsSendingEmailOtp] = useState(false);
  const [isVerifyingEmailOtp, setIsVerifyingEmailOtp] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerificationToken, setEmailVerificationToken] = useState("");

  // Mobile OTP states
  const [mobileOtpDigits, setMobileOtpDigits] = useState<string[]>(Array(6).fill(""));
  const mobileOtpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [mobileOtpError, setMobileOtpError] = useState<string | null>(null);
  const [mobileOtpTimer, setMobileOtpTimer] = useState(0);
  const [isSendingMobileOtp, setIsSendingMobileOtp] = useState(false);
  const [isVerifyingMobileOtp, setIsVerifyingMobileOtp] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [mobileVerificationToken, setMobileVerificationToken] = useState("");

  useEffect(() => {
    api
      .getStats()
      .then((data) => {
        setStats(data);
        setStatsError(false);
      })
      .catch(() => setStatsError(true))
      .finally(() => setStatsLoading(false));
    api
      .getRecentDonors()
      .then((donors) => {
        setRecentDonors(donors);
        setRecentDonorsUnavailable(false);
      })
      .catch(() => {
        setRecentDonors([]);
        setRecentDonorsUnavailable(true);
      });
  }, []);

  // Email resend timer
  useEffect(() => {
    if (emailOtpTimer > 0) {
      const interval = setInterval(() => {
        setEmailOtpTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [emailOtpTimer]);

  // Mobile resend timer
  useEffect(() => {
    if (mobileOtpTimer > 0) {
      const interval = setInterval(() => {
        setMobileOtpTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [mobileOtpTimer]);

  const selectedAmount = amount || customAmount;
  const amountNum = Math.round(parseFloat(selectedAmount)) || 0;
  const panRequired = amountNum >= PAN_THRESHOLD_INR;
  const hasErrors = Object.values(errors).some(Boolean);

  const clearFieldError = (field: keyof FormErrors) => {
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const preventEnterSubmit = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") e.preventDefault();
  };

  /** Phone for email OTP API schema — use real number only when fully valid. */
  const otpCompanionPhone = () =>
    isValidIndianMobile(donor.phone)
      ? formatPhoneInput(donor.phone)
      : "9999999999";

  const validateDonor = (): FormErrors => {
    const next: FormErrors = {};
    if (!donor.firstName.trim()) {
      next.firstName = "First name is required";
    } else if (!isValidPersonName(donor.firstName)) {
      next.firstName = "First name must contain only letters (min 2 characters)";
    }
    if (!donor.lastName.trim()) {
      next.lastName = "Last name is required";
    } else if (!isValidPersonName(donor.lastName)) {
      next.lastName = "Last name must contain only letters (min 2 characters)";
    }
    if (!donor.email.trim()) {
      next.email = "Email is required";
    } else if (!z.string().email().safeParse(donor.email.trim()).success) {
      next.email = "Enter a valid email address";
    }
    if (!donor.phone.trim()) {
      next.phone = "Phone number is required";
    } else if (!isValidIndianMobile(donor.phone)) {
      next.phone = "Enter a valid 10-digit mobile number";
    }
    if (panRequired) {
      if (!donor.pan.trim()) {
        next.pan = "PAN is required for donations of ₹2,000 or more";
      } else if (!isValidPan(donor.pan)) {
        next.pan = "Enter a valid PAN (e.g. ABCDE1234F)";
      }
    }
    return next;
  };

  // Send Email OTP
  const handleSendEmailOtp = async () => {
    if (!donor.email.trim() || !z.string().email().safeParse(donor.email.trim()).success) {
      setErrors((prev) => ({ ...prev, email: "Enter a valid email address first" }));
      return;
    }
    clearFieldError("email");
    setIsSendingEmailOtp(true);
    setEmailOtpError(null);
    try {
      await api.sendOtp({
        email: donor.email.trim(),
        phone: otpCompanionPhone(),
        name: `${donor.firstName} ${donor.lastName}`.trim() || "Donor",
        channel: "email",
      });
      setEmailOtpSent(true);
      setEmailOtpTimer(30);
      setEmailOtpDigits(Array(6).fill(""));
    } catch (err) {
      setEmailOtpError(err instanceof Error ? err.message : "Failed to send OTP. Please try again.");
    } finally {
      setIsSendingEmailOtp(false);
    }
  };

  // Verify Email OTP
  const handleVerifyEmailOtp = async () => {
    const otpCode = emailOtpDigits.join("");
    if (otpCode.length !== 6) {
      setEmailOtpError("Please enter the 6-digit OTP code");
      return;
    }
    setIsVerifyingEmailOtp(true);
    setEmailOtpError(null);
    try {
      const res = await api.verifyOtp({
        email: donor.email.trim(),
        phone: otpCompanionPhone(),
        otp: otpCode,
        channel: "email",
      });
      if (res.success && res.verificationToken) {
        setEmailVerificationToken(res.verificationToken);
        setEmailVerified(true);
      } else {
        setEmailOtpError(res.message || "Invalid OTP code.");
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        setEmailOtpError(err.issues.map(i => i.message).join(", "));
      } else {
        setEmailOtpError(err instanceof Error ? err.message : "Verification failed");
      }
    } finally {
      setIsVerifyingEmailOtp(false);
    }
  };

  // Send Mobile OTP
  const handleSendMobileOtp = async () => {
    if (!donor.email.trim() || !z.string().email().safeParse(donor.email.trim()).success) {
      setErrors((prev) => ({ ...prev, email: "Enter a valid email address first" }));
      return;
    }
    if (!donor.phone.trim() || !isValidIndianMobile(donor.phone)) {
      setErrors((prev) => ({ ...prev, phone: "Enter a valid 10-digit mobile number first" }));
      return;
    }
    clearFieldError("email");
    clearFieldError("phone");
    setIsSendingMobileOtp(true);
    setMobileOtpError(null);
    try {
      await api.sendOtp({
        email: donor.email.trim(),
        phone: formatPhoneInput(donor.phone),
        name: `${donor.firstName} ${donor.lastName}`.trim() || "Donor",
        channel: "mobile",
      });
      setMobileOtpSent(true);
      setMobileOtpTimer(30);
      setMobileOtpDigits(Array(6).fill(""));
    } catch (err) {
      setMobileOtpError(err instanceof Error ? err.message : "Failed to send OTP. Please try again.");
    } finally {
      setIsSendingMobileOtp(false);
    }
  };

  // Verify Mobile OTP
  const handleVerifyMobileOtp = async () => {
    const otpCode = mobileOtpDigits.join("");
    if (otpCode.length !== 6) {
      setMobileOtpError("Please enter the 6-digit OTP code");
      return;
    }
    if (!donor.email.trim() || !z.string().email().safeParse(donor.email.trim()).success) {
      setErrors((prev) => ({ ...prev, email: "Enter a valid email address first" }));
      return;
    }
    setIsVerifyingMobileOtp(true);
    setMobileOtpError(null);
    try {
      const res = await api.verifyOtp({
        email: donor.email.trim(),
        phone: formatPhoneInput(donor.phone),
        otp: otpCode,
        channel: "mobile",
      });
      if (res.success && res.verificationToken) {
        setMobileVerificationToken(res.verificationToken);
        setMobileVerified(true);
      } else {
        setMobileOtpError(res.message || "Invalid OTP code.");
      }
    } catch (err) {
      if (err instanceof z.ZodError) {
        setMobileOtpError(err.issues.map(i => i.message).join(", "));
      } else {
        setMobileOtpError(err instanceof Error ? err.message : "Verification failed");
      }
    } finally {
      setIsVerifyingMobileOtp(false);
    }
  };

  const handleEmailOtpChange = (index: number, value: string) => {
    setEmailOtpError(null);
    const cleanVal = value.replace(/[^0-9]/g, "");
    if (!cleanVal) {
      const nextDigits = [...emailOtpDigits];
      nextDigits[index] = "";
      setEmailOtpDigits(nextDigits);
      return;
    }
    const valToUse = cleanVal[cleanVal.length - 1];
    const nextDigits = [...emailOtpDigits];
    nextDigits[index] = valToUse;
    setEmailOtpDigits(nextDigits);
    if (index < 5) {
      emailOtpRefs.current[index + 1]?.focus();
    }
  };

  const handleEmailOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    preventEnterSubmit(e);
    if (e.key === "Backspace" && !emailOtpDigits[index] && index > 0) {
      emailOtpRefs.current[index - 1]?.focus();
    }
  };

  const handleMobileOtpChange = (index: number, value: string) => {
    setMobileOtpError(null);
    const cleanVal = value.replace(/[^0-9]/g, "");
    if (!cleanVal) {
      const nextDigits = [...mobileOtpDigits];
      nextDigits[index] = "";
      setMobileOtpDigits(nextDigits);
      return;
    }
    const valToUse = cleanVal[cleanVal.length - 1];
    const nextDigits = [...mobileOtpDigits];
    nextDigits[index] = valToUse;
    setMobileOtpDigits(nextDigits);
    if (index < 5) {
      mobileOtpRefs.current[index + 1]?.focus();
    }
  };

  const handleMobileOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    preventEnterSubmit(e);
    if (e.key === "Backspace" && !mobileOtpDigits[index] && index > 0) {
      mobileOtpRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setErrors({});

    if (step === "amount") {
      if (amountNum < 100) {
        const nextErrors = { amount: "Minimum donation is ₹100" };
        setErrors(nextErrors);
        focusFirstError(nextErrors);
        return;
      }
      setStep("donor");
      requestAnimationFrame(() => {
        if (formCardRef.current) {
          scrollToElement(formCardRef.current, { offset: -80 });
        }
      });
      return;
    }

    if (step === "donor") {
      const donorErrors = validateDonor();
      if (Object.keys(donorErrors).length > 0) {
        setErrors(donorErrors);
        focusFirstError(donorErrors);
        return;
      }

      if (!emailVerified || !mobileVerified) {
        return;
      }

      let result;
      try {
        const testMode =
          paymentTest === "fail" || paymentTest === "cancel"
            ? paymentTest
            : undefined;
        result = await checkout(
          {
            amount: amountNum,
            currency: "INR",
            category,
            type: "one-time",
            donationSource: "online",
            donor: {
              ...donor,
              phone: formatPhoneInput(donor.phone),
              pan: donor.pan.trim() ? formatPanInput(donor.pan) : undefined,
              address: donor.address.trim() || undefined,
            },
            emailVerificationToken,
            mobileVerificationToken,
          },
          { paymentTest: testMode }
        );
      } catch {
        return;
      }

      if (result?.cancelled) {
        return;
      }

      if (result?.incomplete) {
        return;
      }

      if (result?.success && result.donationId) {
        setDonationSuccessCreds({
          donationId: result.donationId,
          access: result.successToken,
          token: result.downloadToken,
        });
        router.push(
          `/donate/success?ref=${encodeURIComponent(result.donationId)}`
        );
        return;
      }

      if (result && !result.success) {
        router.push("/donate/failed");
      }
    }
  };

  const ctaLabel =
    step === "amount"
      ? amountNum >= 100
        ? `Continue — ₹${amountNum.toLocaleString("en-IN")}`
        : "Select at least ₹100"
      : `Pay ₹${amountNum.toLocaleString("en-IN")} via Razorpay`;

  const scrollToForm = () => {
    if (formCardRef.current) {
      scrollToElement(formCardRef.current, { offset: -88 });
    }
  };

  return (
    <div className="pb-16">
      <DonateSplitHero onCtaClick={scrollToForm} />
      <DonateStatsStrip
        stats={stats}
        statsLoading={statsLoading}
        statsError={statsError}
      />

      <section className="relative px-4 pt-8">
        <Reveal>
          <DonateStepper step={step} className="mb-8" variant="light" />
        </Reveal>

        <div
          id="donate-form"
          ref={formCardRef}
          className="mx-auto max-w-lg scroll-mt-28"
        >
          <Card className="bg-surface-elevated shadow-xl">
            <CardBody>
              <h2 className="mb-6 text-center text-xl font-bold text-foreground">
                Make a Donation
              </h2>

              <form onSubmit={handleSubmit} noValidate>
                {hasErrors && (
                  <div
                    role="alert"
                    aria-live="polite"
                    className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200"
                  >
                    <span>Please correct the highlighted fields below.</span>
                    <button
                      type="button"
                      onClick={() => setErrors({})}
                      className="shrink-0 rounded p-0.5 hover:bg-red-100 dark:hover:bg-red-900/30"
                      aria-label="Dismiss error summary"
                    >
                      <X size={16} aria-hidden />
                    </button>
                  </div>
                )}

                <AnimatePresence mode="wait">
                  {step === "amount" ? (
                    <motion.div
                      key="amount"
                      initial={reduced ? false : { opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={reduced ? undefined : { opacity: 0, x: -12 }}
                      transition={{ duration: 0.25 }}
                    >
                      <p className="mb-2 text-sm font-semibold">
                        Select Amount (₹)
                      </p>
                      <div
                        role="radiogroup"
                        aria-label="Donation amount"
                        className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-3"
                      >
                        {PRESET_AMOUNTS.map((a) => (
                          <AmountChip
                            key={a}
                            amount={a}
                            selected={amount === String(a)}
                            onSelect={() => {
                              setAmount(String(a));
                              setCustomAmount("");
                              clearFieldError("amount");
                            }}
                          />
                        ))}
                      </div>

                      <Label htmlFor="custom">Or enter custom amount</Label>
                      <Input
                        id="custom"
                        type="number"
                        min={100}
                        step={1}
                        placeholder="Min ₹100"
                        value={customAmount}
                        onChange={(e) => {
                          setCustomAmount(e.target.value);
                          setAmount("");
                          clearFieldError("amount");
                        }}
                        className={cn("mb-1", errors.amount && "border-red-500")}
                        aria-invalid={!!errors.amount}
                        aria-describedby={
                          errors.amount ? "amount-error" : undefined
                        }
                      />
                      {errors.amount && (
                        <p
                          id="amount-error"
                          className="mb-3 text-sm text-red-600 dark:text-red-400"
                        >
                          {errors.amount}
                        </p>
                      )}
                      {!errors.amount && <div className="mb-4" />}

                      <Label htmlFor="category">
                        Where would you like to help?
                      </Label>
                      <select
                        id="category"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="mb-4 w-full rounded-xl border border-border bg-card px-4 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        {CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="donor"
                      initial={reduced ? false : { opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={reduced ? undefined : { opacity: 0, x: -12 }}
                      transition={{ duration: 0.25 }}
                      className="space-y-3"
                    >
                      <div className="mb-1 flex items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setStep("amount");
                            setErrors({});
                          }}
                          className="shrink-0 text-sm text-primary hover:underline"
                        >
                          ← Change amount
                        </button>
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary sm:px-4 sm:py-1.5">
                          Donating {formatCurrency(amountNum)}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="firstName">First Name *</Label>
                          <Input
                             id="firstName"
                             required
                             value={donor.firstName}
                             onChange={(e) => {
                               setDonor({
                                 ...donor,
                                 firstName: formatNameInput(e.target.value),
                               });
                               clearFieldError("firstName");
                             }}
                             className={cn(
                               errors.firstName && "border-red-500"
                             )}
                             aria-invalid={!!errors.firstName}
                             aria-describedby={
                               errors.firstName ? "firstName-error" : undefined
                             }
                          />
                          {errors.firstName && (
                            <p
                              id="firstName-error"
                              className="mt-1 text-sm text-red-600 dark:text-red-400"
                            >
                              {errors.firstName}
                            </p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="lastName">Last Name *</Label>
                          <Input
                            id="lastName"
                            required
                            value={donor.lastName}
                            onChange={(e) => {
                              setDonor({
                                ...donor,
                                lastName: formatNameInput(e.target.value),
                              });
                              clearFieldError("lastName");
                            }}
                            className={cn(errors.lastName && "border-red-500")}
                            aria-invalid={!!errors.lastName}
                            aria-describedby={
                              errors.lastName ? "lastName-error" : undefined
                            }
                          />
                          {errors.lastName && (
                            <p
                              id="lastName-error"
                              className="mt-1 text-sm text-red-600 dark:text-red-400"
                            >
                              {errors.lastName}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Email Form Group with OTP Verification */}
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <div className="flex gap-2">
                          <Input
                            id="email"
                            type="email"
                            required
                            disabled={emailVerified}
                            value={donor.email}
                            onChange={(e) => {
                              setDonor({ ...donor, email: e.target.value });
                              clearFieldError("email");
                              setEmailOtpSent(false);
                              setEmailVerified(false);
                              setEmailVerificationToken("");
                            }}
                            onKeyDown={preventEnterSubmit}
                            className={cn(
                              "flex-1", 
                              errors.email && "border-red-500", 
                              emailVerified && "border-green-500 bg-green-50/10 dark:bg-green-950/10"
                            )}
                            aria-invalid={!!errors.email}
                          />
                          {!emailVerified && (
                            <button
                              type="button"
                              disabled={isSendingEmailOtp || emailOtpTimer > 0}
                              onClick={handleSendEmailOtp}
                              className="rounded-xl border border-primary px-3 text-xs font-semibold text-primary hover:bg-primary/5 disabled:opacity-50 shrink-0"
                            >
                              {isSendingEmailOtp ? "Sending..." : emailOtpSent ? (emailOtpTimer > 0 ? `Resend (${emailOtpTimer}s)` : "Resend") : "Send OTP"}
                            </button>
                          )}
                        </div>
                        {errors.email && (
                          <p id="email-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
                            {errors.email}
                          </p>
                        )}
                        {emailVerified && <p className="mt-1 text-xs font-semibold text-green-600 dark:text-green-400">Email verified successfully! ✓</p>}

                        {/* Inline Email OTP Input */}
                        {emailOtpSent && !emailVerified && (
                          <div className="mt-3 rounded-lg border border-border bg-card/50 p-3 space-y-2">
                            <Label className="text-xs font-semibold">Enter Email OTP</Label>
                            <div className="flex gap-1.5 items-center">
                              <div className="flex gap-1">
                                {emailOtpDigits.map((digit, idx) => (
                                  <input
                                    key={idx}
                                    id={`email-otp-${idx}`}
                                    ref={(el) => {
                                      emailOtpRefs.current[idx] = el;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    pattern="[0-9]*"
                                    value={digit}
                                    onChange={(e) => handleEmailOtpChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleEmailOtpKeyDown(idx, e)}
                                    className="w-8 h-8 text-center text-sm font-bold rounded-md border border-border bg-background focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all"
                                  />
                                ))}
                              </div>
                              <button
                                type="button"
                                disabled={isVerifyingEmailOtp}
                                onClick={handleVerifyEmailOtp}
                                className="ml-auto rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/95 disabled:opacity-50 shrink-0"
                              >
                                {isVerifyingEmailOtp ? "Verifying..." : "Verify"}
                              </button>
                            </div>
                            {emailOtpError && <p className="text-xs text-red-600 dark:text-red-400">{emailOtpError}</p>}
                          </div>
                        )}
                      </div>

                      {/* Phone Form Group with OTP Verification */}
                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <div className="flex gap-2">
                          <Input
                            id="phone"
                            type="tel"
                            required
                            disabled={mobileVerified}
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="10-digit mobile number"
                            value={donor.phone}
                            onChange={(e) => {
                              setDonor({
                                ...donor,
                                phone: formatPhoneInput(e.target.value),
                              });
                              clearFieldError("phone");
                              setMobileOtpSent(false);
                              setMobileVerified(false);
                              setMobileVerificationToken("");
                            }}
                            onKeyDown={preventEnterSubmit}
                            className={cn(
                              "flex-1", 
                              errors.phone && "border-red-500", 
                              mobileVerified && "border-green-500 bg-green-50/10 dark:bg-green-950/10"
                            )}
                            aria-invalid={!!errors.phone}
                          />
                          {!mobileVerified && (
                            <button
                              type="button"
                              disabled={isSendingMobileOtp || mobileOtpTimer > 0}
                              onClick={handleSendMobileOtp}
                              className="rounded-xl border border-primary px-3 text-xs font-semibold text-primary hover:bg-primary/5 disabled:opacity-50 shrink-0"
                            >
                              {isSendingMobileOtp ? "Sending..." : mobileOtpSent ? (mobileOtpTimer > 0 ? `Resend (${mobileOtpTimer}s)` : "Resend") : "Send OTP"}
                            </button>
                          )}
                        </div>
                        {errors.phone && (
                          <p id="phone-error" className="mt-1 text-sm text-red-600 dark:text-red-400">
                            {errors.phone}
                          </p>
                        )}
                        {mobileVerified && <p className="mt-1 text-xs font-semibold text-green-600 dark:text-green-400">Mobile verified successfully! ✓</p>}

                        {/* Inline Mobile OTP Input */}
                        {mobileOtpSent && !mobileVerified && (
                          <div className="mt-3 rounded-lg border border-border bg-card/50 p-3 space-y-2">
                            <Label className="text-xs font-semibold">Enter Mobile OTP</Label>
                            <div className="flex gap-1.5 items-center">
                              <div className="flex gap-1">
                                {mobileOtpDigits.map((digit, idx) => (
                                  <input
                                    key={idx}
                                    id={`mobile-otp-${idx}`}
                                    ref={(el) => {
                                      mobileOtpRefs.current[idx] = el;
                                    }}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    pattern="[0-9]*"
                                    value={digit}
                                    onChange={(e) => handleMobileOtpChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleMobileOtpKeyDown(idx, e)}
                                    className="w-8 h-8 text-center text-sm font-bold rounded-md border border-border bg-background focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 transition-all"
                                  />
                                ))}
                              </div>
                              <button
                                type="button"
                                disabled={isVerifyingMobileOtp}
                                onClick={handleVerifyMobileOtp}
                                className="ml-auto rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary/95 disabled:opacity-50 shrink-0"
                              >
                                {isVerifyingMobileOtp ? "Verifying..." : "Verify"}
                              </button>
                            </div>
                            {mobileOtpError && <p className="text-xs text-red-600 dark:text-red-400">{mobileOtpError}</p>}
                          </div>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="address">Address (optional)</Label>
                        <Textarea
                          id="address"
                          rows={3}
                          autoComplete="street-address"
                          placeholder="For 80G receipt correspondence"
                          value={donor.address}
                          onChange={(e) =>
                            setDonor({ ...donor, address: e.target.value })
                          }
                        />
                      </div>
                      {panRequired ? (
                        <div>
                          <Label htmlFor="pan">
                            PAN (required for 80G receipt above ₹2,000)
                          </Label>
                          <Input
                            id="pan"
                            type="text"
                            required
                            autoComplete="off"
                            inputMode="text"
                            maxLength={10}
                            value={donor.pan}
                            onChange={(e) => {
                              setDonor({
                                ...donor,
                                pan: formatPanInput(e.target.value),
                              });
                              clearFieldError("pan");
                            }}
                            className={cn(errors.pan && "border-red-500")}
                            aria-invalid={!!errors.pan}
                            aria-describedby={
                              errors.pan ? "pan-error" : "pan-hint"
                            }
                            placeholder="ABCDE1234F"
                          />
                          {errors.pan ? (
                            <p
                              id="pan-error"
                              className="mt-1 text-sm text-red-600 dark:text-red-400"
                            >
                              {errors.pan}
                            </p>
                          ) : (
                            <p id="pan-hint" className="mt-1 text-xs text-muted">
                              {site.donate.taxBenefitNote}
                            </p>
                          )}
                        </div>
                      ) : null}
                    </motion.div>
                  )}
                </AnimatePresence>

                {error && (
                  <p
                    role="alert"
                    className="mt-2 text-sm text-red-600 dark:text-red-400"
                  >
                    {error}
                  </p>
                )}

                <MotionButton
                  type="submit"
                  isLoading={isProcessing}
                  loadingText="Processing..."
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-primary bg-primary px-6 py-3 text-base font-medium text-white shadow-sm hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
                  disabled={
                    (step === "amount" && amountNum < 100) ||
                    isProcessing ||
                    (step === "donor" && (!emailVerified || !mobileVerified))
                  }
                >
                  {ctaLabel}
                </MotionButton>

                <p className="mt-3 text-center text-xs text-muted">
                  Secure payment via Razorpay · UPI · Cards · Net Banking
                </p>
              </form>
            </CardBody>
          </Card>
        </div>
      </section>

      <RecentDonorsTeaser
        donors={recentDonors}
        unavailable={recentDonorsUnavailable}
      />
    </div>
  );
}

export function DonateForm() {
  const searchParams = useSearchParams();
  const allowPaymentTest =
    process.env.NEXT_PUBLIC_USE_MOCK_API === "true" ||
    process.env.NODE_ENV === "development";
  const raw = searchParams.get("paymentTest");
  const paymentTestKey =
    allowPaymentTest && (raw === "fail" || raw === "cancel") ? raw : "live";
  return <DonateFormFields key={paymentTestKey} />;
}
